import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { WorkShift } from "../../services";
import { useTypedSelector } from "../../redux/store";
import { sortByNumberOrString } from "../../utils";
import { ColumnId } from "../../constants/tables/accountsWorkShifts";
import { getRoleDisplayName } from "../../assets/languages/langs";

import { isLimit, isOrder } from ".";

const useWorkShiftSubHandler = (): useWorkShiftSubHandler.Props => {
	const { t } = useTranslation();
	const lang = useTypedSelector((state) => state.session.language);
	const taxiServices = useTypedSelector((state) => state.taxiServices.models);

	const getNameTaxiServiceMax = useCallback(
		(items: WorkShift.Model) => {
			const DEFAULT_TEXT = "Zero";

			if (
				!(
					Array.isArray(items.taxiServiceIds) &&
					items.taxiServiceIds?.length
				)
			)
				return DEFAULT_TEXT;

			if (taxiServices.length === items.taxiServiceIds.length)
				return t(["all_taxi_service", "All branches"]);

			const arr =
				items.taxiServiceIds
					?.map(
						(service) =>
							taxiServices?.find((ts) => ts.id === service)
								?.settlement?.[lang] ?? "",
					)
					.sort((a, b) => a.localeCompare(b)) || [];

			const lastElement = arr.at(-1) ?? DEFAULT_TEXT;
			return lastElement;
		},
		[lang, t, taxiServices],
	);

	const getSortAndLimitWorkShiftSubHandler = useCallback(
		(
			{ limit, order }: useWorkShiftSubHandler.SmallSubscribeOptions,
			roleSubscribe: WorkShift.Model[],
		): WorkShift.Model[] => {
			if (!Array.isArray(roleSubscribe)) {
				return [];
			}

			let retval = [...(roleSubscribe || [])];

			if (isOrder(order)) {
				const [[dataKey, sortType] = []] = Object.entries(order || {});
				const isSort =
					typeof sortType === "string" &&
					typeof dataKey === "string" &&
					["asc", "desc"].includes(sortType.toLowerCase());

				if (isSort) {
					const isAscending = sortType.toLowerCase() === "asc";
					const sortOrder = isAscending ? 1 : -1;

					if (dataKey === ColumnId.Active) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.active ? 1 : 0;
							const nextValue = next.active ? 1 : 0;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Name) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getRoleDisplayName(
								prev.name,
								lang,
							);
							const nextValue = getRoleDisplayName(
								next.name,
								lang,
							);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.OpenAndCloseType) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.openAndCloseType ?? "";
							const nextValue = next.openAndCloseType ?? "";
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.TaxiServices) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getNameTaxiServiceMax(prev);
							const nextValue = getNameTaxiServiceMax(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.CreatedAt) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.createdAt
								? Date.parse(prev.createdAt) || 0
								: 0;
							const nextValue = next.createdAt
								? Date.parse(next.createdAt) || 0
								: 0;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}
				}
			}

			if (isLimit(limit)) {
				retval = retval.slice(0, limit);
			}

			return retval;
		},
		[getNameTaxiServiceMax],
	);

	return useMemo(
		() => ({
			getSortAndLimitWorkShiftSubHandler,
		}),
		[getSortAndLimitWorkShiftSubHandler],
	);
};

declare namespace useWorkShiftSubHandler {
	interface Props {
		getSortAndLimitWorkShiftSubHandler: (
			data: SmallSubscribeOptions,
			roleSubscribe: WorkShift.Model[],
		) => WorkShift.Model[];
	}

	interface SmallSubscribeOptions {
		order?: WorkShift.Order;
		limit?: number;
	}
}

export default useWorkShiftSubHandler;
